import { gql } from '@apollo/client';

export const FETCH_CLASSES_BASIC_INFO = gql`
  query FetchClasses($query: SchoolClassQueryInput) {
    findSchoolClass(query: $query) {
      _id
      title
      classCode
      classUniqueCode
      firstDayOfClass
      lastDayOfClass
      primaryTeacherId
      year
      archivedAt
      isDemo
      organizationId
    }
  }
`;

export const FETCH_CLASSES = gql`
  query FetchClasses($query: SchoolClassQueryInput) {
    findSchoolClass(query: $query) {
      _id
      title
      classCode
      classUniqueCode
      firstDayOfClass
      lastDayOfClass
      primaryTeacherId
      year
      students {
        _id
        firstName
        lastName
        name
        avatar
        email
        points {
          lifetime
        }
      }
      archivedAt
      isDemo
      organizationId
    }
  }
`;

export const FETCH_CLASSES_FOR_STUDENT_OOO_BOOKINGS = gql`
  query FetchClasses($query: SchoolClassQueryInput) {
    findSchoolClass(query: $query) {
      _id
      year
      learningSubject
      classCode
    }
  }
`;

export const FETCH_CLASSES_WITH_ORG_SETTINGS = gql`
  query FetchClasses($query: SchoolClassQueryInput) {
    findSchoolClass(query: $query) {
      _id
      title
      classCode
      firstDayOfClass
      lastDayOfClass
      learningSubject
      primaryTeacherId
      primaryTeacher {
        _id
        name
      }
      year
      organizationId
      organization {
        _id
        name
        terms {
          _id
          name
          startDate
          endDate
        }
        settings {
          visibleContentTypesBySubject {
            visibleContentTypes
            learningSubject
          }
          visibleContentTypesForUser
          defaultChineseLocale
        }
      }
    }
  }
`;

export const STAFF_SCHOOL_CLASSES = gql`
  query StaffSchoolClasses {
    staffSchoolClasses {
      _id
      title
      classCode
      classUniqueCode
      firstDayOfClass
      lastDayOfClass
      archivedAt
      year
      googleClassroom {
        courseId
      }
      primaryTeacherId
      studentIds
      students {
        _id
        name
        firstName
        lastName
        chineseFullName
        chineseFirstName
        chineseLastName
        email
      }
      primaryTeacher {
        _id
        name
      }
      organizationId
      studentGroups {
        _id
        name
        studentIds
      }
      hasActiveLessonPlans
    }
  }
`;

export const FETCH_SCHOOL_CLASSES_DASHBOARD = gql`
  query StaffSchoolClasses {
    staffSchoolClasses {
      _id
      title
      classCode
      classUniqueCode
      firstDayOfClass
      lastDayOfClass
      archivedAt
      learningSubject
      year
      studentGroups {
        _id
        name
      }
      settings {
        classThemeId
        enableAIMeecardGrading
        enableAutomaticGCAssignments
        enableAvatars
        enableGames
        enableLeaderboard
        enableLearningPacks
        enableSelfStudyLibrary
        unlockMechanism
        contentTagAliases {
          contentTagId
          alias
        }
        tierTwoMediaRewards
      }
      googleClassroom {
        courseId
      }
      primaryTeacherId
      studentIds
      students {
        _id
        name
        firstName
        lastName
        email
      }
      primaryTeacher {
        _id
        name
      }
      organizationId
      curriculumLevel {
        contentTagId
      }
      organization {
        _id
        name
        terms {
          _id
          name
          startDate
          endDate
        }
        settings {
          visibleContentTypesBySubject {
            visibleContentTypes
            learningSubject
          }
          visibleContentTypesForUser
          defaultChineseLocale
        }
      }
      classPlan {
        examDates
        componentFocuses
        questionFocuses
        chineseTeachingSystem
        extraSupport
        topics
        textbookReferences
        taskFocus
        wordList
        notes
        attachmentFileNames
        lastUpdatedBy
        updatedAt
      }
    }
  }
`;

export const SET_SCHOOL_CLASSES_START_END = gql`
  mutation SetSchoolClassesStartEnd(
    $schoolClassIds: [MongoObjectId!]!
    $start: DateTime
    $end: DateTime
  ) {
    setSchoolClassesStartEnd(schoolClassIds: $schoolClassIds, start: $start, end: $end)
  }
`;

export const SET_CLASS_REWARDS = gql`
  mutation SetClassRewards($data: ClassRewardInput!) {
    setClassRewardSettings(data: $data)
  }
`;

export const SET_CLASS_THEME = gql`
  mutation SetClassTheme($schoolClassId: MongoObjectId!, $classThemeId: MongoObjectId) {
    setClassTheme(schoolClassId: $schoolClassId, classThemeId: $classThemeId) {
      _id
      settings {
        classThemeId
      }
    }
  }
`;

export const FETCH_CLASSES_WITH_STUDENTS = gql`
  query FetchClassesWithStudents($query: SchoolClassQueryInput, $sortBy: SortBy) {
    findSchoolClass(query: $query, sortBy: $sortBy) {
      _id
      title
      year
      defaultQuestionSet
      organizationId
      organization {
        settings {
          visibleContentTypesBySubject {
            visibleContentTypes
            learningSubject
          }
          visibleContentTypesForUser
        }
      }
      classTotalPoints {
        lifetime
        currentWeek
      }
      curriculumLevel {
        title
        contentTagId
      }
      defaultThemeRootTagId
      yearSystem
      classCode
      curriculum {
        _id
        title
      }
      studentIds
      students {
        _id
        firstName
        lastName
        name
        chineseFullName
        chineseFirstName
        chineseLastName
        avatar
        email
        points {
          lifetime
        }
      }
      settings {
        classThemeId
        enableSelfStudyLibrary
        enableAutomaticGCAssignments
        enableGames
        enableLeaderboard
        enableAIMeecardGrading
      }
      studentIds
      learningSubject
      totalWeeklyGoalPoints
      studentsWeeklyGoalPoints
      classPerformanceVisitorIds
      firstDayOfClass
      lastDayOfClass
      primaryTeacherId
      teacherIds
      googleClassroom {
        courseId
      }
      studentGroups {
        _id
        name
        studentIds
      }
    }
  }
`;

export const GENERATE_DEMO_POINTS = gql`
  mutation GenerateDemoPoints($schoolClassId: MongoObjectId!) {
    generateDemoPoints(schoolClassId: $schoolClassId)
  }
`;

export const SET_CLASS_PERFORMANCE_VISITORS = gql`
  mutation SetClassPerformanceVisitors($schoolClassId: MongoObjectId!, $visitorId: MongoObjectId!) {
    setClassPerformanceVisitors(schoolClassId: $schoolClassId, visitorId: $visitorId)
  }
`;

export const CREATE_SCHOOL_CLASS = gql`
  mutation CreateSchoolClass($data: CreateSchoolClassInput!) {
    createSchoolClass(data: $data) {
      _id
      title
      classCode
      classUniqueCode
      settings {
        enableLeaderboard
        enableAvatars
        enableSelfStudyLibrary
        enableGames
        unlockMechanism
        tierTwoMediaRewards
      }
    }
  }
`;

export const UPDATE_SCHOOL_CLASS = gql`
  mutation UpdateSchoolClass($schoolClassId: MongoObjectId!, $data: UpdateSchoolClassInput!) {
    updateSchoolClass(schoolClassId: $schoolClassId, data: $data) {
      _id
      title
      classCode
      classUniqueCode
      curriculumLevel {
        title
        contentTagId
      }
      year
      learningSubject
    }
  }
`;

export const REMOVE_STUDENTS_FROM_CLASS = gql`
  mutation RemoveStudentsFromClass($schoolClassId: MongoObjectId!, $userIds: [MongoObjectId!]!) {
    removeStudentsFromClass(schoolClassId: $schoolClassId, userIds: $userIds) {
      _id
      title
      classCode
      classUniqueCode
      studentIds
      students {
        _id
        firstName
        lastName
        name
        avatar
        email
        points {
          lifetime
        }
      }
      curriculumLevel {
        title
        contentTagId
      }
      year
      learningSubject
    }
  }
`;

export const CREATE_BASIC_SCHOOL_CLASSES = gql`
  mutation CreateBasicSchoolClasses($data: [CreateBasicSchoolClassInput!]!) {
    createBasicSchoolClasses(data: $data) {
      _id
    }
  }
`;

export const CREATE_AND_ASSIGN_STUDENTS_TO_CLASSES = gql`
  mutation assignStudentsToClasses(
    $data: [AssignStudentsToClassesInput!]!
    $isCreatingClass: Boolean
  ) {
    assignStudentsToClasses(data: $data, isCreatingClass: $isCreatingClass) {
      _id
      studentIds
    }
  }
`;

export const COMPLETE_CLASS_SETUP = gql`
  mutation CompleteClassSetup($data: CompleteClassSetupInput!) {
    completeClassSetup(data: $data) {
      _id
      defaultThemeRootTagId
      curriculumLevel {
        contentTagId
        title
      }
      settings {
        classThemeId
      }
    }
  }
`;

export const UPDATE_CLASS_PLAN = gql`
  mutation UpdateClassPlan($schoolClassId: MongoObjectId!, $classPlanInput: ClassPlanInput!) {
    updateClassPlan(schoolClassId: $schoolClassId, classPlanInput: $classPlanInput) {
      _id
      classPlan {
        examDates
        componentFocuses
        questionFocuses
        chineseTeachingSystem
        extraSupport
        topics
        textbookReferences
        taskFocus
        wordList
        notes
        attachmentFileNames
        lastUpdatedBy
        updatedAt
      }
    }
  }
`;

export const UPDATE_CLASS_ARCHIVE_STATUS = gql`
  mutation UpdateClassArchiveStatus($schoolClassId: MongoObjectId!) {
    updateClassArchiveStatus(schoolClassId: $schoolClassId) {
      _id
      archivedAt
    }
  }
`;

export const SCHOOL_CLASS_WITH_STUDENT_POINTS_INFO = gql`
  query SchoolClassWithStudentPointsInfo($schoolClassId: MongoObjectId!) {
    schoolClass(schoolClassId: $schoolClassId) {
      _id
      title
      classCode
      classUniqueCode
      firstDayOfClass
      lastDayOfClass
      primaryTeacherId
      year
      weeklyTopFiveStudents {
        userId
        user {
          _id
          firstName
          lastName
          name
          chineseFullName
          chineseFirstName
          chineseLastName
          avatar
          meeoppAvatar {
            title
            filename
          }
        }
        points
        rank
      }
      students {
        _id
        firstName
        lastName
        name
        avatar
        email
        points {
          currentDay
          currentWeek
          currentWeekTarget
          lifetime
        }
        meeoppAvatar {
          title
          filename
        }
      }
      archivedAt
      isDemo
      organizationId
    }
  }
`;

export const SCHOOL_CLASS_WITH_CLASS_AVATAR_COLLECTION = gql`
  query SchoolClassWithClassAvatarCollection($schoolClassId: MongoObjectId!) {
    schoolClass(schoolClassId: $schoolClassId) {
      _id
      title
      classCode
      classUniqueCode
      studentIds
      classTotalPoints {
        lifetime
        currentWeek
      }
      classAvatarCollection {
        _id
        avatars {
          avatarId
          avatar {
            _id
            title
            filename
          }
          pointsToUnlock
        }
        schoolClassId
      }
    }
  }
`;

export const SCHOOL_CLASS_WEEKLY_PROGRESS = gql`
  query SchoolClassWeeklyProgress($schoolClassId: MongoObjectId!) {
    schoolClass(schoolClassId: $schoolClassId) {
      studentIds
      pastWeekCompletedOrgBookingsNum
      studentNumWithCompletedSoloSessionsWithinWeek
    }
  }
`;

export const FETCH_SCHOOL_CLASSES = gql`
  query FetchSchoolClasses($input: FetchSchoolClassesInput!) {
    fetchSchoolClasses(input: $input) {
      data {
        _id
        classCode
        title
        isDemo
        learningSubject
        primaryTeacher {
          name
          firstName
          lastName
        }
        studentIds
        curriculumLevel {
          contentTagId
        }
        firstDayOfClass
        lastDayOfClass
        year
        academicYear
        totalWeeklyGoalPoints
        classTotalPoints {
          currentWeek
        }
        settings {
          unlockMechanism
          tierTwoMediaRewards
          enableSelfStudyLibrary
          enableLearningPacks
          enableLeaderboard
          enableAvatars
          enableGames
        }
        archivedAt
      }
      total
    }
  }
`;

export const SEND_FEEDBACK_TO_STUDENT = gql`
  mutation SendFeedbackToStudent($data: SendFeedbackInput!) {
    sendFeedbackToStudent(data: $data) {
      _id
      feedbackForStudents {
        studentId
        feedback
      }
    }
  }
`;

export const CLASSES_WITH_STUDENT_GOALS = gql`
  query FetchClasses($query: SchoolClassQueryInput, $date: DateTime!) {
    findSchoolClass(query: $query) {
      _id
      title
      classCode
      firstDayOfClass
      lastDayOfClass
      learningSubject
      primaryTeacherId
      primaryTeacher {
        _id
        name
      }
      year
      organizationId
      organization {
        _id
        name
      }

      totalWeeklyGoalPoints
      studentsWeeklyGoalPoints

      studentWeeklyScores(date: $date) {
        userId
        user {
          _id
          name
          chineseFullName
        }
        points
        rank
      }

      activeClassWeeklyStudentGoals {
        _id
        goalCategory
        goalType
        userId
        user {
          _id
          name
          firstName
          lastName
          chineseFullName
          chineseFirstName
          chineseLastName
          points {
            currentDay
            currentWeek
            currentWeekTarget
            lifetime
          }
        }
        schoolClassId
        goalStartDate
        numberOfWeeks
        goalEndDate
        targetScore
        score
        scoreType
        weeklyData {
          weekNumber
          weekStartDate
          progressScore
          targetScore
          metadata
        }
      }
    }
  }
`;
